import { v4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useTheme } from '@mui/material';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
  DragEventHandler,
  useEffect,
} from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  OnEdgesChange,
  OnNodesChange,
  OnConnect,
  Connection,
  MarkerType,
  addEdge,
  useReactFlow,
} from 'reactflow';

import { CallsMenuEdgeType, CallsMenuStageType } from '@bandido/shared/constants';
import { CommunicationType, IVRActionType, StageNodeData, TimeoutAction } from '@bandido/shared/types';

import { useCreateAgencyIVRMutation, useGetAgencyIVR, useUpdateAgencyIVRMutation } from './queries';
import { QueryKeys } from '../common/types';
import { useSnackBar } from '../common/components/SnackBarContext';
import { INITIAL_STAGE } from './constants';

type CallsMenuContextType = {
  stages: Node<StageNodeData>[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onStageChange: (id: string, data: Partial<StageNodeData>) => void;
  onCanvasClick: () => void;
  onConnect: OnConnect;
  onCreateStage: DragEventHandler<HTMLDivElement>;
  onSelectStage: (id: string) => void;
  onDeleteEdge: (id: string) => void;
  selectedStageId?: string;
  onSaveIvr: () => void;
  isConnectedStage: (id: string) => boolean;
  getParentStageDialOptions: (stageId: string) => Set<string>;
  onSave: () => void;
  isValidConnection: (connection: Connection) => boolean;
  onDeleteStage: (id: string) => void;
};

const CallsMenuContext = createContext<CallsMenuContextType>({
  stages: [],
  edges: [],
  onNodesChange: () => {},
  onEdgesChange: () => {},
  onConnect: () => {},
  onCreateStage: () => {},
  onSelectStage: () => {},
  onDeleteEdge: () => {},
  selectedStageId: undefined,
  onCanvasClick: () => {},
  onSave: () => {},
  onStageChange: () => {},
  isConnectedStage: () => false,
  onSaveIvr: () => {},
  getParentStageDialOptions: () => new Set(),
  isValidConnection: () => false,
  onDeleteStage: () => {},
});

export const CallsMenuContextProvider = ({ children }: PropsWithChildren) => {
  const theme = useTheme();
  const { id: agencyId } = useParams();
  const reactFlowInstance = useReactFlow();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();

  const [stages, setStages] = useState<Node<StageNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<string | undefined>();

  const { data: agencyIVR, isLoading } = useGetAgencyIVR(Number(agencyId));

  const isCreateMode = !agencyIVR;

  const createAgencyIVRMutation = useCreateAgencyIVRMutation(Number(agencyId), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.getAgencyIVR });
      addToast({ message: t('callsMenu.saveSuccess'), color: 'success' });
    },
    onError: () => {
      addToast({ message: t('callsMenu.saveError'), color: 'error' });
    },
  });

  const getParentStageDialOptions = useCallback(
    (stageId: string) => {
      const edge = edges.find((edge) => edge.source === stageId);
      if (!edge) return new Set<string>();

      const parentStage = stages.find((stage) => stage.id === edge.target);
      if (!parentStage) return new Set<string>();

      const childrenEdges = edges.filter((edge) => edge.target === parentStage.id);
      const childrenStages = stages.filter((stage) => childrenEdges.some((edge) => edge.source === stage.id));

      const dialOptions = childrenStages
        .map((stage) => stage.data?.dialOption)
        .filter((opt): opt is string => typeof opt === 'string');

      return new Set<string>(dialOptions);
    },
    [stages, edges],
  );

  const { mutate: update, isLoading: isUpdating } = useUpdateAgencyIVRMutation(Number(agencyId), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.getAgencyIVR });
      addToast({ message: t('callsMenu.saveSuccess'), color: 'success' });
    },
    onError: () => {
      addToast({ message: t('callsMenu.saveError'), color: 'error' });
    },
  });

  useEffect(() => {
    if (agencyIVR) {
      setStages(agencyIVR.value.stages);
      setEdges(agencyIVR.value.edges);
    } else {
      setStages([INITIAL_STAGE]);
    }
  }, [agencyIVR]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setStages((nds) => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  const onSaveIvr = useCallback(() => {
    const value = JSON.stringify({ stages, edges });
    if (isCreateMode) {
      createAgencyIVRMutation.mutate({ agencyId: Number(agencyId), value });
    } else {
      update({ value });
    }
  }, [isCreateMode, createAgencyIVRMutation, update]);

  const onDeleteStage = useCallback(
    (id: string) => {
      const childrenStages = edges.filter((edge) => edge.target === id).map((edge) => edge.source);
      setStages((prevStages) =>
        prevStages
          .filter((stage) => stage.id !== id)
          .map((stage) => {
            if (childrenStages.includes(stage.id)) {
              return { ...stage, data: { ...stage.data, dialOption: '' } };
            }
            return stage;
          }),
      );
      setEdges((prevEdges) => prevEdges.filter((edge) => edge.target !== id && edge.source !== id));
    },
    [edges],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const edge = {
        ...connection,
        type: CallsMenuEdgeType.CUSTOM,
        style: {
          strokeWidth: 2,
          stroke: theme.palette.primary.main,
        },
        markerEnd: {
          width: 12,
          height: 12,
          type: MarkerType.ArrowClosed,
          color: theme.palette.primary.main,
        },
        animated: false,
        id: `${connection.source}-${connection.target}`,
      };
      setEdges((prevEdges) => {
        const newEdges = addEdge(edge, prevEdges);
        return newEdges;
      });
    },
    [setEdges, theme.palette.primary.main],
  );

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const edge = edges.find((edge) => edge.source === connection.source);
      return !edge;
    },
    [edges],
  );

  const onCreateStage = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newStage: Node<StageNodeData> = {
        id: v4(),
        position,
        type,
        data: {
          communicationType: CommunicationType.TEXT_TO_SPEECH,
          ...(type === CallsMenuStageType.ACTION ? { action: IVRActionType.ENTER_EXTENSION } : {}),
          name: 'New Stage',
          timeout: 10,
          timeoutAction: TimeoutAction.EXIT,
          audioUrl: '',
          text: '',
        },
      };
      setStages((prevStages) => [...prevStages, newStage]);
    },
    [reactFlowInstance],
  );

  const isConnectedStage = useCallback((stageId: string) => edges.some((edge) => edge.source === stageId), [edges]);

  const onCanvasClick = useCallback(() => {
    setSelectedStageId(undefined);
  }, []);

  const onDeleteEdge = useCallback(
    (id: string) => {
      const childrenStage = edges.find((edge) => edge.id === id)?.source;

      if (childrenStage) {
        setStages((prevStages) =>
          prevStages.map((stage) =>
            stage.id === childrenStage ? { ...stage, data: { ...stage.data, dialOption: '' } } : stage,
          ),
        );
      }

      setEdges((prevEdges) => prevEdges.filter((edge) => edge.id !== id));
    },
    [edges],
  );

  const onStageChange = useCallback((id: string, data: Partial<StageNodeData>) => {
    setStages((prevStages) =>
      prevStages.map((stage) => (stage.id === id ? { ...stage, data: { ...stage.data, ...data } } : stage)),
    );
  }, []);

  const onSave = useCallback(() => {
    const value = JSON.stringify({ stages, edges });
    if (isCreateMode) {
      createAgencyIVRMutation.mutate({ agencyId: Number(agencyId), value });
    } else {
      update({ value });
    }
  }, [isCreateMode, createAgencyIVRMutation, update]);

  const onSelectStage = useCallback((id: string) => {
    setSelectedStageId(id);
  }, []);

  return (
    <CallsMenuContext.Provider
      value={{
        stages,
        edges,
        onConnect,
        onNodesChange,
        isValidConnection,
        onEdgesChange,
        onDeleteStage,
        onCreateStage,
        onSaveIvr,
        selectedStageId,
        onSelectStage,
        onSave,
        onDeleteEdge,
        onCanvasClick,
        onStageChange,
        isConnectedStage,
        getParentStageDialOptions,
      }}
    >
      {children}
    </CallsMenuContext.Provider>
  );
};

export const useCallsMenu = () => {
  const context = useContext(CallsMenuContext);
  if (!context) {
    throw new Error('useCallsMenu must be used within a CallsMenuProvider');
  }
  return context;
};
